import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Clock, ChevronRight } from 'lucide-react';
import { API } from '@/App';

const ArticlesPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50" data-testid="articles-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-gray-600 hover:text-gray-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Publications Scientifiques
          </h1>
          <p className="text-lg text-gray-600">
            Consultez les dernières publications et recherches en contactologie
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12" data-testid="loading">
            <div className="animate-pulse text-[#1e3a5f]">Chargement...</div>
          </div>
        ) : articles.length === 0 ? (
          <Card className="border-2 border-gray-100" data-testid="no-articles">
            <CardContent className="p-12 text-center">
              <FileText className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg">Aucun article disponible pour le moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {articles.map((article, index) => (
              <Card
                key={article.id}
                className="border-2 border-gray-100 card-hover cursor-pointer"
                onClick={() => navigate(`/articles/${article.id}`)}
                data-testid={`article-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-testid={`article-title-${index}`}>
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {article.content.substring(0, 200)}...
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <Clock className="mr-2" size={16} />
                          {new Date(article.created_at).toLocaleDateString('fr-FR')}
                        </div>
                        <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-gray-600">Par {article.author}</span>
                      </div>
                      {article.pdf_url && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/articles/${article.id}/view`);
                          }}
                          className="bg-cyan-600 hover:bg-cyan-700"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Voir le document
                        </Button>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-400 rounded-xl flex items-center justify-center">
                        <FileText className="text-white" size={24} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesPage;
